import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Grid,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  Tab,
  Tabs,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip
} from '@mui/material';
import {
  Save,
  Email,
  Payment,
  Security,
  Notifications,
  Settings,
  Add,
  Delete,
  Edit
} from '@mui/icons-material';
import axios from '../utils/axios';

const SystemConfiguration = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  // Configuration states
  const [generalConfig, setGeneralConfig] = useState({
    siteName: '',
    siteDescription: '',
    contactEmail: '',
    supportEmail: '',
    maintenanceMode: false,
    registrationEnabled: true,
    emailVerificationRequired: true
  });

  const [emailConfig, setEmailConfig] = useState({
    provider: 'smtp',
    smtpHost: '',
    smtpPort: 587,
    smtpUser: '',
    smtpPassword: '',
    smtpSecure: true,
    fromEmail: '',
    fromName: ''
  });

  const [paymentConfig, setPaymentConfig] = useState({
    stripeEnabled: true,
    stripePublicKey: '',
    stripeSecretKey: '',
    stripeWebhookSecret: '',
    currency: 'USD',
    taxRate: 0
  });

  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [planDialog, setPlanDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState({
    name: '',
    price: '',
    frequency: 'monthly',
    features: [],
    isActive: true
  });

  const [notificationSettings, setNotificationSettings] = useState({
    welcomeEmailEnabled: true,
    subscriptionRenewalReminder: true,
    eventReminder: true,
    eventReminderDays: 1,
    paymentFailureNotification: true,
    adminNotificationEmail: ''
  });

  useEffect(() => {
    fetchConfigurations();
  }, []);

  const fetchConfigurations = async () => {
    try {
      setLoading(true);
      const [generalRes, emailRes, paymentRes, plansRes, notificationRes] = await Promise.all([
        axios.get('/api/config/general'),
        axios.get('/api/config/email'),
        axios.get('/api/config/payment'),
        axios.get('/api/subscriptions/plans'),
        axios.get('/api/config/notifications')
      ]);

      setGeneralConfig(generalRes.data);
      setEmailConfig(emailRes.data);
      setPaymentConfig(paymentRes.data);
      setSubscriptionPlans(plansRes.data);
      setNotificationSettings(notificationRes.data);
    } catch (error) {
      setErrorMessage('Failed to load configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGeneral = async () => {
    try {
      await axios.put('/api/config/general', generalConfig);
      setSuccessMessage('General settings saved successfully');
    } catch (error) {
      setErrorMessage('Failed to save general settings');
    }
  };

  const handleSaveEmail = async () => {
    try {
      await axios.put('/api/config/email', emailConfig);
      setSuccessMessage('Email settings saved successfully');
    } catch (error) {
      setErrorMessage('Failed to save email settings');
    }
  };

  const handleSavePayment = async () => {
    try {
      await axios.put('/api/config/payment', paymentConfig);
      setSuccessMessage('Payment settings saved successfully');
    } catch (error) {
      setErrorMessage('Failed to save payment settings');
    }
  };

  const handleSaveNotifications = async () => {
    try {
      await axios.put('/api/config/notifications', notificationSettings);
      setSuccessMessage('Notification settings saved successfully');
    } catch (error) {
      setErrorMessage('Failed to save notification settings');
    }
  };

  const handleSavePlan = async () => {
    try {
      if (editingPlan) {
        await axios.put(`/api/subscriptions/plans/${editingPlan._id}`, planForm);
      } else {
        await axios.post('/api/subscriptions/plans', planForm);
      }
      setSuccessMessage('Plan saved successfully');
      setPlanDialog(false);
      fetchConfigurations();
      resetPlanForm();
    } catch (error) {
      setErrorMessage('Failed to save plan');
    }
  };

  const handleDeletePlan = async (planId) => {
    if (window.confirm('Are you sure you want to delete this plan?')) {
      try {
        await axios.delete(`/api/subscriptions/plans/${planId}`);
        setSuccessMessage('Plan deleted successfully');
        fetchConfigurations();
      } catch (error) {
        setErrorMessage('Failed to delete plan');
      }
    }
  };

  const resetPlanForm = () => {
    setPlanForm({
      name: '',
      price: '',
      frequency: 'monthly',
      features: [],
      isActive: true
    });
    setEditingPlan(null);
  };

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        System Configuration
      </Typography>

      <Paper elevation={3}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab icon={<Settings />} label="General" />
          <Tab icon={<Email />} label="Email" />
          <Tab icon={<Payment />} label="Payment" />
          <Tab icon={<Notifications />} label="Notifications" />
          <Tab icon={<Security />} label="Plans" />
        </Tabs>

        {/* General Settings */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Site Name"
                value={generalConfig.siteName}
                onChange={(e) => setGeneralConfig({ ...generalConfig, siteName: e.target.value })}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Email"
                value={generalConfig.contactEmail}
                onChange={(e) => setGeneralConfig({ ...generalConfig, contactEmail: e.target.value })}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Site Description"
                value={generalConfig.siteDescription}
                onChange={(e) => setGeneralConfig({ ...generalConfig, siteDescription: e.target.value })}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Support Email"
                value={generalConfig.supportEmail}
                onChange={(e) => setGeneralConfig({ ...generalConfig, supportEmail: e.target.value })}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={generalConfig.maintenanceMode}
                    onChange={(e) => setGeneralConfig({ ...generalConfig, maintenanceMode: e.target.checked })}
                  />
                }
                label="Maintenance Mode"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={generalConfig.registrationEnabled}
                    onChange={(e) => setGeneralConfig({ ...generalConfig, registrationEnabled: e.target.checked })}
                  />
                }
                label="Registration Enabled"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={generalConfig.emailVerificationRequired}
                    onChange={(e) => setGeneralConfig({ ...generalConfig, emailVerificationRequired: e.target.checked })}
                  />
                }
                label="Email Verification Required"
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSaveGeneral}
              >
                Save General Settings
              </Button>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Email Settings */}
        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Email Provider</InputLabel>
                <Select
                  value={emailConfig.provider}
                  onChange={(e) => setEmailConfig({ ...emailConfig, provider: e.target.value })}
                >
                  <MenuItem value="smtp">SMTP</MenuItem>
                  <MenuItem value="sendgrid">SendGrid</MenuItem>
                  <MenuItem value="mailgun">Mailgun</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="SMTP Host"
                value={emailConfig.smtpHost}
                onChange={(e) => setEmailConfig({ ...emailConfig, smtpHost: e.target.value })}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="SMTP Port"
                type="number"
                value={emailConfig.smtpPort}
                onChange={(e) => setEmailConfig({ ...emailConfig, smtpPort: e.target.value })}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="SMTP User"
                value={emailConfig.smtpUser}
                onChange={(e) => setEmailConfig({ ...emailConfig, smtpUser: e.target.value })}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="SMTP Password"
                type="password"
                value={emailConfig.smtpPassword}
                onChange={(e) => setEmailConfig({ ...emailConfig, smtpPassword: e.target.value })}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={emailConfig.smtpSecure}
                    onChange={(e) => setEmailConfig({ ...emailConfig, smtpSecure: e.target.checked })}
                  />
                }
                label="Use Secure Connection (TLS)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="From Email"
                value={emailConfig.fromEmail}
                onChange={(e) => setEmailConfig({ ...emailConfig, fromEmail: e.target.value })}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="From Name"
                value={emailConfig.fromName}
                onChange={(e) => setEmailConfig({ ...emailConfig, fromName: e.target.value })}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSaveEmail}
              >
                Save Email Settings
              </Button>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Payment Settings */}
        <TabPanel value={activeTab} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={paymentConfig.stripeEnabled}
                    onChange={(e) => setPaymentConfig({ ...paymentConfig, stripeEnabled: e.target.checked })}
                  />
                }
                label="Enable Stripe Payments"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Stripe Public Key"
                value={paymentConfig.stripePublicKey}
                onChange={(e) => setPaymentConfig({ ...paymentConfig, stripePublicKey: e.target.value })}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Stripe Secret Key"
                type="password"
                value={paymentConfig.stripeSecretKey}
                onChange={(e) => setPaymentConfig({ ...paymentConfig, stripeSecretKey: e.target.value })}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Stripe Webhook Secret"
                type="password"
                value={paymentConfig.stripeWebhookSecret}
                onChange={(e) => setPaymentConfig({ ...paymentConfig, stripeWebhookSecret: e.target.value })}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Currency</InputLabel>
                <Select
                  value={paymentConfig.currency}
                  onChange={(e) => setPaymentConfig({ ...paymentConfig, currency: e.target.value })}
                >
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="GBP">GBP</MenuItem>
                  <MenuItem value="CAD">CAD</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tax Rate (%)"
                type="number"
                value={paymentConfig.taxRate}
                onChange={(e) => setPaymentConfig({ ...paymentConfig, taxRate: e.target.value })}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSavePayment}
              >
                Save Payment Settings
              </Button>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Notification Settings */}
        <TabPanel value={activeTab} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.welcomeEmailEnabled}
                    onChange={(e) => setNotificationSettings({ ...notificationSettings, welcomeEmailEnabled: e.target.checked })}
                  />
                }
                label="Send Welcome Email"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.subscriptionRenewalReminder}
                    onChange={(e) => setNotificationSettings({ ...notificationSettings, subscriptionRenewalReminder: e.target.checked })}
                  />
                }
                label="Send Subscription Renewal Reminders"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.eventReminder}
                    onChange={(e) => setNotificationSettings({ ...notificationSettings, eventReminder: e.target.checked })}
                  />
                }
                label="Send Event Reminders"
              />
            </Grid>
            {notificationSettings.eventReminder && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Event Reminder Days Before"
                  type="number"
                  value={notificationSettings.eventReminderDays}
                  onChange={(e) => setNotificationSettings({ ...notificationSettings, eventReminderDays: e.target.value })}
                  margin="normal"
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.paymentFailureNotification}
                    onChange={(e) => setNotificationSettings({ ...notificationSettings, paymentFailureNotification: e.target.checked })}
                  />
                }
                label="Send Payment Failure Notifications"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Admin Notification Email"
                value={notificationSettings.adminNotificationEmail}
                onChange={(e) => setNotificationSettings({ ...notificationSettings, adminNotificationEmail: e.target.value })}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSaveNotifications}
              >
                Save Notification Settings
              </Button>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Subscription Plans */}
        <TabPanel value={activeTab} index={4}>
          <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Subscription Plans</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                resetPlanForm();
                setPlanDialog(true);
              }}
            >
              Add Plan
            </Button>
          </Box>
          <List>
            {subscriptionPlans.map((plan) => (
              <Card key={plan._id} sx={{ mb: 2 }}>
                <ListItem>
                  <ListItemText
                    primary={plan.name}
                    secondary={
                      <Box>
                        <Typography variant="body2">
                          ${plan.price} / {plan.frequency}
                        </Typography>
                        <Box mt={1}>
                          {plan.features.map((feature, idx) => (
                            <Chip
                              key={idx}
                              label={feature}
                              size="small"
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          ))}
                        </Box>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => {
                        setEditingPlan(plan);
                        setPlanForm(plan);
                        setPlanDialog(true);
                      }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() => handleDeletePlan(plan._id)}
                    >
                      <Delete />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </Card>
            ))}
          </List>
        </TabPanel>
      </Paper>

      {/* Plan Dialog */}
      <Dialog open={planDialog} onClose={() => setPlanDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPlan ? 'Edit Plan' : 'Add Plan'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Plan Name"
            value={planForm.name}
            onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Price"
            type="number"
            value={planForm.price}
            onChange={(e) => setPlanForm({ ...planForm, price: e.target.value })}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Frequency</InputLabel>
            <Select
              value={planForm.frequency}
              onChange={(e) => setPlanForm({ ...planForm, frequency: e.target.value })}
            >
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="quarterly">Quarterly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Features (comma separated)"
            value={planForm.features.join(', ')}
            onChange={(e) => setPlanForm({ ...planForm, features: e.target.value.split(',').map(f => f.trim()) })}
            margin="normal"
            multiline
            rows={3}
          />
          <FormControlLabel
            control={
              <Switch
                checked={planForm.isActive}
                onChange={(e) => setPlanForm({ ...planForm, isActive: e.target.checked })}
              />
            }
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanDialog(false)}>Cancel</Button>
          <Button onClick={handleSavePlan} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Messages */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage('')}
      >
        <Alert severity="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SystemConfiguration;